import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { FiArrowRight, FiCalendar, FiCheckCircle, FiMail, FiPhone, FiShield, FiX } from 'react-icons/fi';
import { siteConfig } from './siteConfig';
import { normalizeAustralianMobile, isValidContactPhone } from './leadValidation.mjs';
import { buildUniversalEnquiry } from './universalEnquiry.mjs';
import './EnquirySheet.css';

const INTERESTS = ['Excel, Forms & reporting','Shelter & booking management','Case management & compliance','Website & digital improvements','Something else'];
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const freshData = () => ({ name:'',organisation:'',email:'',phone:'',message:'',marketingConsent:false,smsConsent:false,website:'' });
const newId=()=> typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : '';

export default function EnquirySheet({open,onClose,initialInterest}){
  const reduced = useReducedMotion();
  const modalRef = useRef(null);
  const focusRef = useRef(null);
  const submissionId = useRef(newId());
  const openedAt = useRef(Date.now());
  const [data,setData] = useState(freshData);
  const [interest,setInterest] = useState(initialInterest || INTERESTS[0]);
  const [phase,setPhase] = useState('form');
  const [error,setError] = useState('');
  const [reference,setReference] = useState('');
  const [delivery,setDelivery] = useState('');
  const [gatewayReady,setGatewayReady] = useState(null);
  const [gatewayIssue,setGatewayIssue] = useState('');
  const [gatewayFields,setGatewayFields] = useState(null);
  const [acknowledgementEnabled,setAcknowledgementEnabled] = useState(true);
  const [emailFallback,setEmailFallback] = useState(false);
  // The calendar redirect starts ONLY after the Gateway accepts the enquiry.
  // Never redirect on failed or timed-out submissions.
  const [redirectSeconds,setRedirectSeconds] = useState(3);
  const [redirectPaused,setRedirectPaused] = useState(false);
  const redirectedRef = useRef(false);
  const bookingSuccessRef = useRef(null);

  useEffect(()=>{if(!open)return;
    openedAt.current=Date.now();
    submissionId.current=newId();
    setInterest(initialInterest||INTERESTS[0]);
    setPhase('form');setData(freshData());setError('');setReference('');setDelivery('');setEmailFallback(false);
    setRedirectSeconds(3);setRedirectPaused(false);redirectedRef.current=false;
  },[open,initialInterest]);

  useEffect(()=>{
    if(!open)return;
    const panel=modalRef.current;
    const originalOverflow=document.body.style.overflow;
    const originalPosition=document.body.style.position;
    const originalTop=document.body.style.top;
    const originalWidth=document.body.style.width;
    const scrollY=window.scrollY;
    document.body.style.overflow='hidden';
    document.body.style.position='fixed';
    document.body.style.top='-'+scrollY+'px';
    document.body.style.width='100%';
    const viewport=window.visualViewport;
    const resize=()=>{
      if(!panel)return;
      const h=viewport?.height||window.innerHeight;
      const top=viewport?.offsetTop||0;
      panel.style.setProperty('--mobile-visual-height',h+'px');
      panel.style.setProperty('--mobile-visual-top',top+'px');
      // The sheet is anchored to the visible viewport when the iOS keyboard opens.
      panel.parentElement?.style.setProperty('--mobile-visual-height',h+'px');
      panel.parentElement?.style.setProperty('--mobile-visual-top',top+'px');
    };
    resize();
    viewport?.addEventListener('resize',resize);
    viewport?.addEventListener('scroll',resize);
    const onKey=(event)=>{
      if(event.key==='Escape'){event.preventDefault();onClose();return;}
      if(event.key!=='Tab'||!panel)return;
      const nodes=Array.from(panel.querySelectorAll('button:not(:disabled), input:not(:disabled), textarea:not(:disabled), a[href]')).filter(el=>el.getClientRects().length);
      if(!nodes.length)return;
      if(event.shiftKey && document.activeElement===nodes[0]){event.preventDefault();nodes[nodes.length-1].focus();}
      if(!event.shiftKey && document.activeElement===nodes[nodes.length-1]){event.preventDefault();nodes[0].focus();}
    };
    document.addEventListener('keydown',onKey);
    const previousFocus=document.activeElement;
    // Do not auto-focus a text input and summon the iOS keyboard as soon as the sheet opens.
    panel.querySelector('.enquiry-close')?.focus({preventScroll:true});
    return ()=>{
      viewport?.removeEventListener('resize',resize);viewport?.removeEventListener('scroll',resize);
      document.removeEventListener('keydown',onKey);
      document.body.style.overflow=originalOverflow;document.body.style.position=originalPosition;
      document.body.style.top=originalTop;document.body.style.width=originalWidth;
      window.scrollTo(0,scrollY);
      if(previousFocus?.isConnected)previousFocus.focus({preventScroll:true});
    };
  },[open,onClose]);

  useEffect(()=>{
    if(!open || !siteConfig.leadEndpoint)return;
    let active=true;
    const abort=new AbortController();
    setGatewayReady(null);
    setGatewayIssue('');
    setGatewayFields(null);
    setAcknowledgementEnabled(true);
    (async()=>{
      try{
        const timer=setTimeout(()=>abort.abort(),7000);
        let response;
        try{
          response=await fetch(siteConfig.leadEndpoint,{method:'GET',mode:'cors',signal:abort.signal,cache:'no-store'});
        }finally{clearTimeout(timer);}
        const status=await response.json().catch(()=>null);
        if(!active)return;
        if(response.status===404){
          setGatewayReady(false);
          setGatewayIssue('missing_endpoint');
        }else if(response.ok && status?.ready===true){
          setGatewayReady(true);
          setGatewayIssue('');
          setGatewayFields(Array.isArray(status.form?.fields)?status.form.fields:null);
          setAcknowledgementEnabled(status.form?.sendAcknowledgement===true);
        }else{
          setGatewayReady(false);
          setGatewayIssue('unavailable');
        }
      }catch(_){
        if(active){setGatewayReady(false);setGatewayIssue('unavailable');}
      }
    })();
    return()=>{active=false;abort.abort();};
  },[open]);

  // A short, user-visible, cancellable transition to the external calendar.
  // Using same-tab navigation avoids popup blockers; cleanup cancels pending
  // navigation whenever the modal closes or the confirmation is unmounted.
  useEffect(()=>{
    if(!open || phase!=='success' || redirectPaused || redirectedRef.current || !siteConfig.bookingUrl)return;
    if(redirectSeconds===0){
      redirectedRef.current=true;
      try{window.location.assign(siteConfig.bookingUrl);}
      catch(_){setRedirectPaused(true);}
      return;
    }
    const timer=window.setTimeout(()=>setRedirectSeconds(seconds=>Math.max(0,seconds-1)),1000);
    return()=>window.clearTimeout(timer);
  },[open,phase,redirectSeconds,redirectPaused]);

  useEffect(()=>{
    if(open && phase==='success')bookingSuccessRef.current?.focus({preventScroll:true});
  },[open,phase]);

  const phoneRaw = data.phone.trim();
  const normalizedMobile = normalizeAustralianMobile(phoneRaw);
  const draftSubject = '[BPA Shelter Website] Enquiry from ' + (data.organisation.trim() || 'a community service');
  const draftBody = [
    'Enquiry for BPA Solutions', '',
    'Name: ' + data.name.trim(),
    'Organisation: ' + data.organisation.trim(),
    'Email: ' + data.email.trim(),
    'Phone: ' + (phoneRaw || 'Not provided'),
    'Area of interest: ' + interest,
    'How we can help: ' + (data.message.trim() || 'Not provided'),
    'Marketing consent: ' + (data.marketingConsent ? 'Yes' : 'No'),
    'Source: BPA Shelter & Community Services Website'
  ].join('\n');
  const emailDraftLink = 'mailto:' + siteConfig.contactEmail + '?subject=' + encodeURIComponent(draftSubject) + '&body=' + encodeURIComponent(draftBody);
  function field(key,value){setData(prev=>({...prev,[key]:value,...(key==='phone'&&!normalizeAustralianMobile(value)?{smsConsent:false}:{})}));}
  function inputFocus(e){setTimeout(()=>{e.target.scrollIntoView({block:'nearest',behavior:'smooth'});},160);}
  async function submit(event){
    event.preventDefault();
    if(phase==='sending')return;
    setEmailFallback(false);
    if(data.name.trim().length<2 || data.organisation.trim().length<2 || !EMAIL_REGEX.test(data.email.trim())){
      setError('Please enter your name, organisation and a valid work email.');return;
    }
    if(!isValidContactPhone(phoneRaw)){setError('Please enter a valid contact number, such as 0412 345 678.');return;}
    if(data.smsConsent && !normalizedMobile){setError('SMS requires an Australian mobile number (04… or +614…).');return;}
    if(!siteConfig.leadEndpoint){
      setError('Online enquiries are not configured yet. You can send this enquiry from your email application instead.');
      setEmailFallback(true);return;
    }
    if(!submissionId.current){setError('Your browser could not create a submission reference. Please reload the page.');return;}
    setError('');setPhase('sending');
    try{
      const payload = buildUniversalEnquiry({
        data,interest,formFields:gatewayFields,submissionId:submissionId.current,
        startedAt:openedAt.current
      });
      const controller=new AbortController();
      const timeout=setTimeout(()=>controller.abort(),28000);
      let response;
      try{
        response=await fetch(siteConfig.leadEndpoint,{method:'POST',mode:'cors',
          headers:{'Content-Type':'application/json'},
          body:JSON.stringify(payload),
          signal:controller.signal
        });
      }finally{clearTimeout(timeout);}
      const result=await response.json().catch(()=>({ok:false,message:'Server response unavailable.'}));
      if(response.status===404)throw new Error('The online enquiry endpoint is unavailable. Please use the prefilled email option below until it has been activated.');
      if(!response.ok || !result.ok)throw new Error(result.message||'The enquiry could not be sent. Please try again.');
      if(result.accepted!==true)throw new Error('The enquiry has not been confirmed as accepted. Please check before booking.');
      setReference(result.reference||'');
      setDelivery(result.acknowledgement==='not_queued'?'acknowledgement_pending':result.delivery||'queued');
      // The Gateway returned an accepted enquiry: proceed to the calendar only now.
      setRedirectSeconds(3);
      setRedirectPaused(false);
      redirectedRef.current=false;
      setPhase('success');
      setEmailFallback(false);
    }catch(err){
      setPhase('form');
      setEmailFallback(true);
      setError(err?.name==='AbortError'
        ? 'The request timed out. It may have been received. Check your email before trying again.'
        : err instanceof TypeError
          ? 'The Email Gateway could not be reached. Nothing has been confirmed as sent.'
          : (err.message || 'The enquiry could not be sent. Please try again.'));

    }
  }
  const form=<AnimatePresence>{open&&<div className="enquiry-root" role="presentation">
    <motion.button type="button" className="enquiry-scrim" aria-label="Close enquiry form" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={onClose}/>
    <motion.div ref={modalRef} className="enquiry-sheet" role="dialog" aria-modal="true" aria-labelledby="lead-form-title" tabIndex={-1}
      initial={reduced?false:{opacity:0,y:16,scale:.97}} animate={{opacity:1,y:0,scale:1}} exit={reduced?{}:{opacity:0,y:12,scale:.985}} transition={{type:'spring',damping:32,stiffness:310}}>
      <div className="enquiry-sheet-header"><span className="enquiry-handle"/><div><span className="overline">A PRACTICAL PLACE TO START</span><h2 id="lead-form-title">Schedule a complimentary 30 minute automation session</h2><p>Tell us what you would like to automate. Once your enquiry is accepted, you will be taken to our calendar to select a 30-minute Teams or Zoom consultation.</p></div><button type="button" aria-label="Close form" className="enquiry-close" onClick={onClose}><FiX/></button></div>
      {phase==='success'?<div className="enquiry-success booking-transition" ref={bookingSuccessRef} tabIndex={-1}>
        <div className="booking-status-mark"><FiCheckCircle/><span>STEP 1 OF 2 COMPLETE</span></div>
        <h3>Enquiry received. Let's secure your consultation time.</h3>
        <p>{delivery==='acknowledgement_pending'
          ? 'Your enquiry was accepted. The acknowledgement email could not be queued, but you can still choose an available consultation time.'
          : !acknowledgementEnabled
            ? 'Your enquiry was accepted by BPA Solutions. Next, select a time that suits you.'
            : 'Your enquiry was accepted and your email acknowledgement has been queued or processed. Next, select a time that suits you.'}</p>
        <div className="booking-next-step">
          <span className="booking-step-label"><FiCalendar/> STEP 2 OF 2</span>
          <strong>Choose your preferred 30-minute session</strong>
          <span>You'll continue to our secure booking calendar to select an available time.</span>
          {!redirectPaused && siteConfig.bookingUrl ? <>
            <div className="booking-progress-head" role="status" aria-live="polite"><span>Taking you to the booking calendar</span><b>{redirectSeconds}s</b></div>
            <div className="booking-progress-track" role="progressbar" aria-label="Time until booking calendar opens" aria-valuemin={0} aria-valuemax={3} aria-valuenow={3-redirectSeconds}>
              <div className="booking-progress-fill" style={{width:((3-redirectSeconds)/3*100)+'%'}}/>
            </div>
          </> : <p className="booking-paused-note" role="status">{siteConfig.bookingUrl?'Automatic redirect paused. You can continue whenever you are ready.':'The booking calendar is temporarily unavailable. Please contact BPA Solutions to arrange your session.'}</p>}
        </div>
        {siteConfig.bookingUrl && <a className="primary-btn enquiry-full booking-continue" href={siteConfig.bookingUrl} rel="noreferrer">Continue to booking calendar <FiArrowRight/></a>}
        <div className="booking-footer-actions">
          {!redirectPaused && siteConfig.bookingUrl && <button type="button" className="booking-pause" onClick={()=>setRedirectPaused(true)}>Stay on this page</button>}
          {redirectPaused && siteConfig.bookingUrl && <button type="button" className="booking-pause" onClick={()=>{setRedirectSeconds(3);setRedirectPaused(false);redirectedRef.current=false}}>Resume automatic redirect</button>}
          <button type="button" className="booking-pause" onClick={onClose}>Close</button>
        </div>
        <div className="booking-reference"><FiShield/><span>Enquiry reference: <b>{reference||'Shown in your confirmation'}</b></span></div>
        <p className="booking-confirmation-note">Your enquiry has been received. Your consultation is not booked until you select an available slot and complete the booking calendar.</p>
      </div>:

      <form className="lead-form" onSubmit={submit} noValidate>
        <fieldset className="enquiry-interests"><legend>What would you like to see?</legend><div>{INTERESTS.map(option=><button type="button" key={option} aria-pressed={interest===option} className={interest===option?'selected':''} onClick={()=>setInterest(option)}>{option}</button>)}</div></fieldset>
        <div className="enquiry-fields"><label>Your name *<input ref={focusRef} autoComplete="name" value={data.name} required maxLength={100} placeholder="Full name" onFocus={inputFocus} onChange={e=>field('name',e.target.value)}/></label>
          <label>Organisation *<input value={data.organisation} maxLength={140} required placeholder="Your service or organisation" onFocus={inputFocus} onChange={e=>field('organisation',e.target.value)}/></label>
          <label>Work email *<input value={data.email} type="email" inputMode="email" autoComplete="email" maxLength={180} required placeholder="you@organisation.org.au" onFocus={inputFocus} onChange={e=>field('email',e.target.value)}/></label>
          <label>Phone <small>(optional)</small><input type="tel" inputMode="tel" autoComplete="tel" value={data.phone} maxLength={40} placeholder="0412 345 678" onFocus={inputFocus} onChange={e=>field('phone',e.target.value)}/></label></div>
        {phoneRaw&&normalizedMobile&&<p className="enquiry-phone-preview"><FiPhone/> Mobile number recognised: <strong>{normalizedMobile}</strong></p>}
        <label>What would you like to improve? <small>(optional)</small><textarea rows={2} maxLength={2000} value={data.message} placeholder="e.g. Our compliance reports take hours to prepare" onFocus={inputFocus} onChange={e=>field('message',e.target.value)}/></label>
        <div className="enquiry-consents">
          <label className="enquiry-consent"><input type="checkbox" checked={data.marketingConsent} onChange={e=>field('marketingConsent',e.target.checked)}/><span><strong>I'd like BPA Solutions updates and relevant offers.</strong><small>Optional marketing consent for email. You can withdraw consent at any time.</small></span></label>
          {siteConfig.smsAcknowledgementEnabled&&normalizedMobile&&<label className="enquiry-consent"><input type="checkbox" checked={data.smsConsent} onChange={e=>field('smsConsent',e.target.checked)}/><span><strong>Send an SMS acknowledgement to my mobile.</strong><small>Optional transactional message. This is separate from marketing consent.</small></span></label>}
        </div>
        <div className="enquiry-honeypot" aria-hidden="true"><label>Leave this field empty<input value={data.website} autoComplete="off" tabIndex={-1} onChange={e=>field('website',e.target.value)}/></label></div>
        {error&&<p role="alert" className="enquiry-error">{error}</p>}
        {gatewayReady===false&&<p className="enquiry-offline" role="status"><FiShield/> {gatewayIssue==='missing_endpoint'
          ? 'The enquiry endpoint is not available. You can still enter your details and use the email alternative if sending fails.'
          : 'The Gateway has not confirmed readiness. You can try sending, and an email alternative will be available if it fails.'}</p>}
        <button disabled={phase==='sending'} type="submit" className="primary-btn enquiry-full">{phase==='sending'?'Submitting enquiry…':'Request my complimentary 30 minute session'} <FiArrowRight/></button>
        {emailFallback&&<a className="enquiry-fallback" href={emailDraftLink}><FiMail/> Open prefilled email in my email app <FiArrowRight/></a>}
        {emailFallback&&<p className="enquiry-fallback-note">This alternative opens your email application. You must press Send there. It is not an automatic website submission.</p>}
        <p className="enquiry-privacy"><FiShield/> By submitting, you agree to be contacted about this enquiry. Marketing is optional. Do not include resident or confidential case information. Read our <a href={siteConfig.privacyUrl} target="_blank" rel="noreferrer">Privacy Policy</a>.</p>
      </form>}
      <div className="enquiry-bottom-contact"><a href="mailto:support@bpasolutions.au"><FiMail/> Email BPA Solutions</a><a href="tel:+61425228778"><FiPhone/> 0425 228 778</a></div>
    </motion.div>
  </div>}</AnimatePresence>;
  return createPortal(form,document.body);
}